import { AutoAwesome } from '@mui/icons-material';
import { Box, Button, Stack, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue } from 'recoil';
import PseudonymizeCheckbox from '~/components/ModelSelection/PseudonymizeCheckbox';
import { usePrimaryImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { PredictionModel } from '~/utils/transforms/predictionModelsDataTransforms';
import DoseStructuresTable from './DoseStructuresTable';
import ModelParameters from './ModelParameters';
import ModelSelect from './ModelSelect';

export default function DoseModelSelection({
    availableModels,
    isLoadingModels,
    showPseudonymization,
    isLoadingSession,
    isSubmitting,
    onSubmit,
}: {
    availableModels?: PredictionModel[];
    isLoadingModels: boolean;
    showPseudonymization: boolean;
    isLoadingSession?: boolean;
    isSubmitting?: boolean;
    onSubmit: (modelName: string) => void;
}) {
    const dosePredictionTask = useRecoilValue(dosePredictionTaskState);
    // The task ships `getSlicesAsDicomBlobs()`; on an incomplete image that
    // yields only the slices that arrived, and the model answers for a volume
    // that is not the patient's without anything looking wrong.
    const imageGate = usePrimaryImageCompletenessGate();
    return (
        <Box display="flex" flexDirection="column" gap={1.5}>
            <Box display="flex" gap={1}>
                <ModelSelect
                    availableModels={availableModels}
                    isLoadingModels={isLoadingModels}
                />
            </Box>
            <DoseStructuresTable isLoading={isLoadingSession} />
            <Box>
                <Box display="flex" justifyContent="space-between">
                    <ModelParameters
                        modelName={dosePredictionTask?.model?.label}
                        targets={dosePredictionTask?.targets}
                        version={dosePredictionTask?.model?.version}
                        isLatest={dosePredictionTask?.model?.isLatest}
                    />
                    <Stack gap={0.5} alignItems="flex-end">
                        <Stack alignItems="flex-start">
                            {showPseudonymization && <PseudonymizeCheckbox />}
                        </Stack>
                        <Tooltip
                            title={
                                imageGate.isBlocked ? (
                                    imageGate.reason
                                ) : !dosePredictionTask?.model?.name ? (
                                    <FormattedMessage id="selectAModelToStart" />
                                ) : (
                                    ''
                                )
                            }
                            placement="left-start"
                        >
                            {/* Wrapper needed for MUI Tooltip to work on disabled buttons */}
                            <Box component="span">
                                <Button
                                    variant="contained"
                                    color="primary"
                                    endIcon={<AutoAwesome />}
                                    onClick={() =>
                                        onSubmit(
                                            dosePredictionTask?.model!.name
                                        )
                                    }
                                    disabled={
                                        !dosePredictionTask?.model?.name ||
                                        isSubmitting ||
                                        !dosePredictionTask.targets?.some(
                                            (t) => t.isTarget
                                        ) ||
                                        imageGate.isBlocked
                                    }
                                    sx={{ width: 160 }}
                                    data-testid="start-dose-prediction-button"
                                >
                                    <FormattedMessage id="start" />
                                </Button>
                            </Box>
                        </Tooltip>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
}
